import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../hooks/useApi';
import Icon from './Icon';

const LABELS_CHAMPS = {
  operateurs: 'Opérateurs',
  heure_debut: 'Heure début',
  heure_fin: 'Heure fin',
  notes: 'Notes',
  temp_conservation: 'Temp. conservation',
  saison: 'Saison',
  evenement_id: 'Événement lié',
  lot_fournisseur: 'Lot fournisseur',
  date_achat_matiere: 'Date achat MP',
  date_expiration_matiere: 'Date expiration MP',
  date_congelation: 'Date congélation',
  date_expiration_produit: 'Date expiration produit',
  fournisseur_specifique: 'Fournisseur (spécifique)',
  lot_specifique: 'Lot (spécifique)',
  date_achat_specifique: 'Date achat (spécifique)',
  date_expiration_specifique: 'Date expiration (spécifique)',
};

const LABELS_TYPES = {
  incident: 'Incident / déviation',
  note_corrective: 'Note corrective',
  courbe_temperature: 'Courbe température',
};

function tronquer(str, max = 80) {
  if (!str) return '—';
  if (str.length <= max) return str;
  return str.slice(0, max) + '…';
}

function formatDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('fr-CA', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return d;
  }
}

function ligneVide(msg) {
  return (
    <tr>
      <td colSpan={6}>
        <div className="empty-state" style={{ padding: 'var(--space-5) 0' }}>
          <Icon name="liste" size={24} />
          <span>{msg}</span>
        </div>
      </td>
    </tr>
  );
}

export default function AuditTrailBatch({ batchId }) {
  const [audit, setAudit] = useState([]);
  const [corrections, setCorrections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState('');
  const [onglet, setOnglet] = useState('modifications');
  const [corrigeeEtendue, setCorrigeeEtendue] = useState(null); // id de la correction étendue

  const charger = useCallback(() => {
    if (!batchId) return;
    setLoading(true);
    setErreur('');
    api.get(`/batches/${batchId}/audit-trail`)
      .then(data => {
        // backend retourne ASC, on inverse pour le plus récent en haut
        setAudit([...(data.audit || [])].reverse());
        setCorrections([...(data.corrections || [])].reverse());
      })
      .catch(e => {
        setErreur(e?.response?.data?.error || 'Impossible de charger l\'historique');
      })
      .finally(() => setLoading(false));
  }, [batchId]);

  useEffect(() => { charger(); }, [charger]);

  // Exposer refresh via ref (optionnel, parent peut rappeler charger via key)
  // Mais on expose via window event pour que FormCorrectionAppend puisse le déclencher
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.batchId === batchId) charger();
    };
    window.addEventListener('audit-trail-refresh', handler);
    return () => window.removeEventListener('audit-trail-refresh', handler);
  }, [batchId, charger]);

  if (!batchId) return null;

  return (
    <div className="card" style={{ marginTop: 'var(--space-5)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-4)' }}>
        <h3 style={{ margin: 0, fontFamily: 'Cinzel, serif', fontSize: 14, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-soft)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon name="liste" size={15} /> Historique du batch finalisé
        </h3>
        <button
          className="btn btn-ghost btn--sm"
          onClick={charger}
          disabled={loading}
        >
          <Icon name="rafraichir" size={13} />
          {loading ? 'Chargement...' : 'Rafraîchir'}
        </button>
      </div>

      {/* Onglets */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 'var(--space-4)', borderBottom: '1px solid var(--border)' }}>
        {[
          { id: 'modifications', label: `Modifications${audit.length ? ` (${audit.length})` : ''}` },
          { id: 'corrections', label: `Corrections${corrections.length ? ` (${corrections.length})` : ''}` },
        ].map(o => (
          <button
            key={o.id}
            onClick={() => setOnglet(o.id)}
            style={{
              background: 'none',
              border: 'none',
              borderBottom: onglet === o.id ? '2px solid var(--accent)' : '2px solid transparent',
              color: onglet === o.id ? 'var(--accent)' : 'var(--muted)',
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: onglet === o.id ? 700 : 400,
              padding: '8px 16px',
              cursor: 'pointer',
              transition: 'color var(--t-fast)',
            }}
          >
            {o.label}
          </button>
        ))}
      </div>

      {erreur && (
        <div className="error-inline" style={{ marginBottom: 'var(--space-3)' }}>
          <Icon name="erreur" size={15} /> {erreur}
        </div>
      )}

      {loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <div className="skeleton" style={{ height: 36, borderRadius: 'var(--radius-sm)' }} />
          <div className="skeleton" style={{ height: 36, borderRadius: 'var(--radius-sm)' }} />
        </div>
      )}

      {!loading && onglet === 'modifications' && (
        <div style={{ overflowX: 'auto' }}>
          <table className="esc-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Utilisateur</th>
                <th>Champ</th>
                <th>Ancienne valeur</th>
                <th>Nouvelle valeur</th>
                <th>Raison</th>
              </tr>
            </thead>
            <tbody>
              {audit.length === 0
                ? ligneVide('Aucune modification post-finalisation pour ce batch.')
                : audit.map((row, i) => (
                  <tr key={row.id ?? i}>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{formatDate(row.timestamp || row.date_modif || row.created_at)}</td>
                    <td>
                      <span className="badge" style={{ textTransform: 'uppercase' }}>
                        {row.user_code || row.utilisateur || '—'}
                      </span>
                    </td>
                    <td>
                      <span style={{ color: 'var(--accent)', fontWeight: 600, fontSize: 13 }}>
                        {LABELS_CHAMPS[row.champ] || row.champ}
                      </span>
                    </td>
                    <td>
                      <span className="muted" style={{ fontSize: 12, fontStyle: 'italic' }}>
                        {tronquer(row.ancienne_valeur) || '(vide)'}
                      </span>
                    </td>
                    <td>
                      <span style={{ color: 'var(--success)', fontSize: 12 }}>
                        {tronquer(row.nouvelle_valeur) || '(vide)'}
                      </span>
                    </td>
                    <td>
                      <span className="muted" style={{ fontSize: 12 }}>
                        {row.raison ? tronquer(row.raison, 60) : <em>—</em>}
                      </span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && onglet === 'corrections' && (
        <div style={{ overflowX: 'auto' }}>
          <table className="esc-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Utilisateur</th>
                <th>Type</th>
                <th>Contenu</th>
              </tr>
            </thead>
            <tbody>
              {corrections.length === 0
                ? ligneVide('Aucune correction append-only pour ce batch.')
                : corrections.map((row, i) => {
                  const estEtendu = corrigeeEtendue === (row.id ?? i);
                  return (
                    <tr key={row.id ?? i}>
                      <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{formatDate(row.timestamp || row.created_at)}</td>
                      <td>
                        <span className="badge" style={{ textTransform: 'uppercase' }}>
                          {row.user_code || row.utilisateur || '—'}
                        </span>
                      </td>
                      <td>
                        <span className={
                          row.type === 'incident'
                            ? 'badge badge--danger'
                            : row.type === 'note_corrective'
                              ? 'badge badge--info'
                              : 'badge badge--gold'
                        }>
                          {LABELS_TYPES[row.type] || row.type}
                        </span>
                      </td>
                      <td style={{ maxWidth: 340 }}>
                        {estEtendu ? (
                          <span style={{ fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {row.contenu}
                            <button
                              className="btn btn-ghost btn--sm"
                              style={{ marginLeft: 8 }}
                              onClick={() => setCorrigeeEtendue(null)}
                            >
                              voir moins
                            </button>
                          </span>
                        ) : (
                          <span style={{ fontSize: 13 }}>
                            {tronquer(row.contenu)}
                            {row.contenu?.length > 80 && (
                              <button
                                className="btn btn-ghost btn--sm"
                                style={{ marginLeft: 6 }}
                                onClick={() => setCorrigeeEtendue(row.id ?? i)}
                              >
                                voir +
                              </button>
                            )}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
